import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { EventForMomentCard } from '../domain/interfaces/event';
import {
  createAddTournamentUserToTeamReducer,
  createDeleteTournamentUserReducer,
  createFetchUserEventsReducer,
  createKickTournamentUserFromTeamReducer,
  createUpsertTournamentUserReducer,
} from './reducers';

/**
 * Список игроков в слайсе больше не живёт: `/players` читает его через RTK Query
 * (`playersApi`), а перечитывает — инвалидацией тега `PLAYERS_LIST_TAG`. Здесь
 * остались только флаги мутаций и моменты игрока со своей клиентской нарезкой.
 */
export interface PlayersSliceState {
  events: { [playerId: number]: EventForMomentCard[] };
  currentPlayerId?: number;

  isProcessing: boolean;
  isProcessed: boolean;
  processName?: string;

  moments: {
    perPage: number;
    loadLimit: number;

    pages: number;
    currentPage: number;

    allDataLoaded: boolean;
  };
}

const initialState: PlayersSliceState = {
  events: {},

  isProcessing: false,
  isProcessed: false,

  moments: {
    perPage: Number(localStorage.getItem('momentsPerPage')) || 9,
    loadLimit: 20,

    pages: 0,
    currentPage: 0,

    allDataLoaded: false,
  },
};

export const playersSlice = createSlice({
  name: 'players',
  initialState,
  reducers: {
    resetProcess: (state) => {
      state.isProcessing = false;
      state.isProcessed = false;
      state.processName = undefined;
    },
    setMomentsPerPage: (state, action: PayloadAction<{ perPage: number }>) => {
      state.moments.perPage = action.payload.perPage;
      localStorage.setItem('momentsPerPage', state.moments.perPage.toString());

      const currentPlayerId = state.currentPlayerId;
      if (currentPlayerId === undefined) return;

      state.moments.pages = Math.ceil(state.events[currentPlayerId]?.length / state.moments.perPage);
      state.moments.currentPage = state.moments.currentPage > state.moments.pages ? state.moments.pages : state.moments.currentPage;
    },
    setMomentsCurrentPage: (state, action: PayloadAction<{ currentPage: number }>) => {
      state.moments.currentPage = action.payload.currentPage;
    },
    setCurrentPlayerId: (state, action: PayloadAction<{ playerId: number }>) => {
      state.currentPlayerId = action.payload.playerId;
    },
    resetMomentsData: (state) => {
      state.moments = {
        perPage: Number(localStorage.getItem('momentsPerPage')) || 9,
        loadLimit: 20,

        pages: 0,
        currentPage: 0,

        allDataLoaded: false,
      };
    },
  },
  extraReducers: (builder) => {
    createUpsertTournamentUserReducer(builder);
    createDeleteTournamentUserReducer(builder);
    createAddTournamentUserToTeamReducer(builder);
    createKickTournamentUserFromTeamReducer(builder);
    createFetchUserEventsReducer(builder);
  },
});

export const { setMomentsPerPage, setMomentsCurrentPage, setCurrentPlayerId, resetMomentsData } = playersSlice.actions;

export default playersSlice.reducer;
export * from './selectors';
